import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import utils from '../../utils/utils';
import PropTypes from 'prop-types';
import AdminPayoutReportRow from '../payoutReports/adminPayoutReportRow';
import Helmet from 'react-helmet';
import CustomPaginator from '../../components/paginator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { CSVLink } from 'react-csv';
import { usePayoutReports, usePayoutReportsEdit } from './apiHooks';
import Loader from '../../components/loader';
import ErrorMessage from '../../components/errorMessage';

const RESULTS_PER_PAGE = 100;

export function getBatchPaymentCsv(payoutReports) {
  const csvHeaders = [
    'name',
    'email',
    'paymentReference',
    'receiverType',
    'amountCurrency',
    'amount',
    'sourceCurrency',
    'targetCurrency',
    'IBAN',
  ];

  const csvRows = payoutReports.map((payoutReport) => {
    return [
      payoutReport.recipientName,
      '',
      `Membo payout ${payoutReport.id}`,
      'INSTITUTION',
      'target',
      payoutReport.amount,
      'EUR',
      'EUR',
      payoutReport.iban,
    ];
  });

  return [csvHeaders, ...csvRows];
}

const AdminPayoutReports = ({ setLoading, setError, history }) => {
  const [activePage, setActivePage] = useState(1);
  const [sortBy, setSortBy] = useState('idDesc');
  const [selectedPayoutReports, setSelectedPayoutReports] = useState([]);

  const {
    isFetching: loading,
    result,
    isError,
    error,
    refetch,
  } = usePayoutReports({
    sortBy: sortBy,
    max: RESULTS_PER_PAGE,
    offset: (activePage - 1) * RESULTS_PER_PAGE,
  });

  const {
    editPayoutReports,
    isLoading: editLoading,
    isError: isEditError,
    error: editError,
  } = usePayoutReportsEdit(() =>
    history.push({
      pathname: '/admin/payoutReports',
    })
  );

  useEffect(() => {
    refetch();
  }, [activePage, sortBy]);

  function setSortMethod(newSortBy) {
    let sortParam;

    switch (newSortBy) {
      case 'id':
      default:
        sortParam = sortBy === 'idAsc' ? 'idDesc' : 'idAsc';
    }

    setSortBy(sortParam);
  }

  const { formatMessage } = useIntl();

  const paginator = (
    <div className="m-b-3 m-t-3">
      <CustomPaginator
        activePage={activePage}
        resultsPerPage={RESULTS_PER_PAGE}
        setActivePage={(newNumber) => {
          if (newNumber !== activePage) {
            setActivePage(newNumber);
          }
        }}
        totalCount={result?.totalCount || 0}
      />
    </div>
  );

  const selectPayoutReport = (index) => {
    if (selectedPayoutReports.length) {
      const newSelectedReports = [...selectedPayoutReports];
      const existingSelectedIndex = selectedPayoutReports.findIndex((spr) => spr.id === result.payoutReports[index].id);
      if (existingSelectedIndex >= 0) {
        newSelectedReports.splice(existingSelectedIndex, 1);
      } else {
        newSelectedReports.push(result.payoutReports[index]);
      }

      setSelectedPayoutReports(newSelectedReports);
      return;
    }

    setSelectedPayoutReports([result.payoutReports[index]]);
  };

  return (
    <div>
      <Helmet>
        <title>{formatMessage({ id: 'admin.payout.reports.title' })}</title>
      </Helmet>

      <h3 className="m-b-3">
        <FormattedMessage id={'admin.payout.reports.title'} />
      </h3>

      <Loader show={loading || editLoading} />
      {isError || isEditError ? <ErrorMessage error={utils.getErrorMessageFromResponse(error || editError)} /> : ''}

      {selectedPayoutReports.length ? (
        <div className="m-b-3">
          <CSVLink
            className="btn btn-small btn-default m-r-2"
            data={getBatchPaymentCsv(selectedPayoutReports)}
            filename={`wiseBatchPayment.csv`}
          >
            <FontAwesomeIcon icon={faDownload} /> Get batch payment CSV
          </CSVLink>
          <button
            className="btn btn-small btn-default"
            onClick={() => editPayoutReports({ payoutReports: selectedPayoutReports })}
          >
            Mark selected as PAID
          </button>
        </div>
      ) : (
        ''
      )}

      {paginator}

      <table className="table table-striped admin__table admin__table m-bottom-margin">
        <thead>
          <tr>
            <th onClick={() => setSortMethod('id')} className="sortable" scope="col">
              Id
            </th>
            <th scope="col">Marketplace</th>
            <th scope="col">Amount</th>
            <th scope="col">Status</th>
            <th scope="col">Our commission</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {result?.payoutReports.map((payoutReport, index) => {
            const isSelected = selectedPayoutReports.find((pr) => pr.id === payoutReport.id);

            return (
              <AdminPayoutReportRow
                payoutReport={payoutReport}
                selectPayoutReport={() => selectPayoutReport(index)}
                isSelected={isSelected}
                refetch={refetch}
                key={index}
              />
            );
          })}
        </tbody>
      </table>

      {paginator}
    </div>
  );
};

AdminPayoutReports.propTypes = {
  setLoading: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
};

AdminPayoutReports.defaultProps = {};

export default AdminPayoutReports;
